(() => {
  const WRAPPER = '[data-header-wrapper]';
  const PANEL = '[data-hover-disclosure]';
  const VISIBLE = 'is-visible';
  const RESIZING = 'is-resizing';
  const CLOSING = 'is-closing';
  const DURATION = 340;
  const CLOSE_DURATION = 260;
  const HANDOFF = 300;

  const instances = new Map();

  class DropdownMorph {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.panels = Array.from(wrapper.querySelectorAll(PANEL));
      this.visible = new Set();
      this.timers = new Map();
      this.closedPanel = null;
      this.closedAt = 0;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.observer = new MutationObserver(this.onMutate.bind(this));

      this.panels.forEach((panel) => {
        if (panel.classList.contains(VISIBLE)) {
          this.visible.add(panel);
        }
        this.observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
      });

      this.onResize = this.resetAll.bind(this);
      window.addEventListener('resize', this.onResize);
    }

    unload() {
      this.observer.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.resetAll();
    }

    onMutate(records) {
      const opened = [];

      records.forEach((record) => {
        const panel = record.target;
        const isVisible = panel.classList.contains(VISIBLE);
        const wasVisible = this.visible.has(panel);

        if (isVisible === wasVisible) return;

        if (isVisible) {
          this.visible.add(panel);
          opened.push(panel);
        } else {
          this.visible.delete(panel);
          this.close(panel);
        }
      });

      opened.forEach((panel) => this.expand(panel));
    }

    expand(panel) {
      this.clearTimer(panel);
      panel.classList.remove(CLOSING);
      panel.style.height = '';

      this.panels.forEach((other) => {
        other.style.zIndex = other === panel ? '2' : '1';
      });

      if (this.reduceMotion.matches) return;

      const source = this.morphSource(panel);
      if (!source) return;

      const from = source.getBoundingClientRect().height;
      const to = this.measure(panel);

      if (!from || !to || Math.round(from) === Math.round(to)) return;

      this.resize(panel, from, to);

      this.panels.forEach((other) => {
        if (other === panel) return;
        if (!this.visible.has(other) && !other.classList.contains(CLOSING)) return;
        this.resize(other, from, to);
      });

      this.timers.set(
        panel,
        window.setTimeout(() => this.settle(panel), DURATION + 30)
      );
    }

    resize(panel, from, to) {
      panel.classList.add(RESIZING);
      panel.style.height = `${from}px`;
      panel.getBoundingClientRect();
      panel.style.height = `${to}px`;
    }

    morphSource(panel) {
      for (const other of this.visible) {
        if (other !== panel) return other;
      }

      const handedOver =
        this.closedPanel &&
        this.closedPanel !== panel &&
        performance.now() - this.closedAt < HANDOFF;

      return handedOver ? this.closedPanel : null;
    }

    measure(panel) {
      const cached = panel.style.height;
      panel.style.height = 'auto';
      const height = panel.getBoundingClientRect().height;
      panel.style.height = cached;
      return height;
    }

    settle(panel) {
      this.timers.delete(panel);
      panel.classList.remove(RESIZING);
      panel.style.height = '';
    }

    close(panel) {
      this.clearTimer(panel);
      this.closedPanel = panel;
      this.closedAt = performance.now();

      if (this.reduceMotion.matches) {
        this.clear(panel);
        return;
      }

      panel.classList.add(CLOSING);
      this.timers.set(
        panel,
        window.setTimeout(() => this.clear(panel), CLOSE_DURATION)
      );
    }

    clearTimer(panel) {
      window.clearTimeout(this.timers.get(panel));
      this.timers.delete(panel);
    }

    clear(panel) {
      this.clearTimer(panel);
      panel.classList.remove(RESIZING);
      panel.classList.remove(CLOSING);
      panel.style.height = '';
      panel.style.zIndex = '';
    }

    resetAll() {
      this.closedPanel = null;
      this.panels.forEach((panel) => this.clear(panel));
    }
  }

  const init = () => {
    document.querySelectorAll(WRAPPER).forEach((wrapper) => {
      if (instances.has(wrapper)) return;
      instances.set(wrapper, new DropdownMorph(wrapper));
    });
  };

  const prune = () => {
    instances.forEach((instance, wrapper) => {
      if (document.contains(wrapper)) return;
      instance.unload();
      instances.delete(wrapper);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    prune();
    init();
  });

  document.addEventListener('shopify:section:unload', prune);
})();
